import React from 'react';

import {POP_QUIZ_PHASE} from './pop-quiz/constants';
import {PopQuizPromptPhase} from './pop-quiz/pop-quiz-prompt-phase.react-component';
import {PopQuizQuizPhase} from './pop-quiz/pop-quiz-quiz-phase.react-component';
import {PopQuizResultsPhase} from './pop-quiz/pop-quiz-results-phase.react-component';
import {PopQuizSnoozedPhase} from './pop-quiz/pop-quiz-snoozed-phase.react-component';
import {PopQuizModalProps} from './pop-quiz/types';
import {usePopQuiz} from './pop-quiz/use-pop-quiz';

// --- Component ---

// A pure phase router: all quiz selection, data-fetching, and lifecycle state lives behind
// usePopQuiz() (see pop-quiz/use-pop-quiz.ts) — this component just renders whichever phase
// is currently active.
export function PopQuizModal(props: PopQuizModalProps): React.ReactElement | null {
    const {enablePopQuizPP, popQuizV2ExperienceEnabled} = props;

    const {
        quizData,
        phase,
        isOpen,
        questions,
        correctCount,
        attemptId,
        stopShowing,
        setStopShowing,
        recommendedAssessment,
        canRequestDifferentQuiz,
        onAccept,
        onComplete,
        onPromptDismiss,
        onRequestDifferentQuiz,
        onSnoozingExit,
        onSnoozedClose,
        onFeedback,
        onRecommendedAssessmentSelected,
        onContinueLearning,
    } = usePopQuiz(props);

    if (!enablePopQuizPP || !quizData) {
        return null;
    }

    return (
        <>
            {phase === POP_QUIZ_PHASE.prompt && (
                <PopQuizPromptPhase
                    isOpen={isOpen}
                    quizData={quizData}
                    stopShowing={stopShowing}
                    setStopShowing={setStopShowing}
                    onAccept={onAccept}
                    onDismiss={onPromptDismiss}
                    recommendedAssessment={recommendedAssessment}
                    onRecommendedAssessmentSelected={onRecommendedAssessmentSelected}
                    onRequestDifferentQuiz={
                        canRequestDifferentQuiz ? onRequestDifferentQuiz : undefined
                    }
                />
            )}

            {phase === POP_QUIZ_PHASE.snoozed && (
                <PopQuizSnoozedPhase isOpen={isOpen} onClose={onSnoozedClose} />
            )}

            {phase === POP_QUIZ_PHASE.quiz && (
                <PopQuizQuizPhase
                    isOpen={isOpen}
                    quizData={quizData}
                    questions={questions}
                    attemptId={attemptId}
                    stopShowing={stopShowing}
                    setStopShowing={setStopShowing}
                    onComplete={onComplete}
                    onExit={onSnoozingExit}
                    popQuizV2ExperienceEnabled={popQuizV2ExperienceEnabled}
                />
            )}

            {phase === POP_QUIZ_PHASE.results && (
                <PopQuizResultsPhase
                    isOpen={isOpen}
                    quizData={quizData}
                    correctCount={correctCount}
                    totalQuestions={questions.length}
                    stopShowing={stopShowing}
                    setStopShowing={setStopShowing}
                    onExit={onSnoozingExit}
                    onContinueLearning={onContinueLearning}
                    onFeedback={onFeedback}
                    popQuizV2ExperienceEnabled={popQuizV2ExperienceEnabled}
                />
            )}
        </>
    );
}
